export interface MimeAttachmentContent {
  filename: string;
  mimeType: string;
  content?: string | Uint8Array;
  url?: string;
}

export const fileTypeToMimeType = (fileType: string): string => {
  const ext = fileType.toLowerCase().replace(/^\./, "");
  const map: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
  };
  return map[ext] || "application/octet-stream";
};

export const resolveFileExtension = (fileType: string | undefined, url: string): string => {
  if (fileType) {
    return fileType.toLowerCase().replace(/^\./, "");
  }
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".pdf")) {
    return "pdf";
  }
  return path.split(".").pop()?.toLowerCase() ?? "";
};

const normalizeFetchedBlob = (blob: Blob, mimeType: string): Blob =>
  !mimeType || blob.type === mimeType ? blob : new Blob([blob], { type: mimeType });

const resolveAttachmentBlob = async (attachment: MimeAttachmentContent): Promise<Blob> => {
  if (attachment.url) {
    const res = await fetch(attachment.url);
    const blob = await res.blob();
    return normalizeFetchedBlob(blob, attachment.mimeType);
  }
  if (attachment.content != null) {
    return attachmentContentToBlob(attachment);
  }
  throw new Error("Attachment has no content or url");
};

export type AttachmentPreviewKind = "image" | "pdf" | "text" | "unsupported";

export const attachmentContentToBlob = (attachment: MimeAttachmentContent): Blob => {
  if (attachment.content == null) {
    throw new Error("Attachment content is required");
  }
  let blobContent: BlobPart;
  if (typeof attachment.content === "string") {
    const binary = atob(attachment.content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    blobContent = bytes;
  } else {
    blobContent = attachment.content;
  }
  return new Blob([blobContent], { type: attachment.mimeType });
};

export const getAttachmentPreviewKind = (
  mimeType: string,
  filename: string
): AttachmentPreviewKind => {
  const lower = mimeType.toLowerCase();
  const ext = (filename.split(".").pop() || "").toLowerCase();

  if (lower.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  if (lower === "application/pdf" || ext === "pdf") {
    return "pdf";
  }
  if (
    lower.startsWith("text/") ||
    lower === "application/json" ||
    ["txt", "csv", "json", "xml", "html", "htm", "log", "md"].includes(ext)
  ) {
    return "text";
  }
  return "unsupported";
};

export const canPreviewAttachment = (mimeType: string, filename: string): boolean =>
  getAttachmentPreviewKind(mimeType, filename) !== "unsupported";

export const downloadAttachmentBlob = (attachment: MimeAttachmentContent): void => {
  void resolveAttachmentBlob(attachment).then((blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};
